<template>
  <div class="guide-text-tags">
    <h2>What are text tags?</h2>
    <p>
      Text tags are marker strings embedded directly in document content, typically
      inside DOCX templates, that the system auto-detects and converts into signing
      fields. Think of them as placeholders that say "put a signature here" or "put
      a date field here." When the document is uploaded and processed, the platform
      scans the text, finds these markers, and creates the corresponding annotation
      fields at the exact positions where the tags appear.
    </p>
    <p>
      Tags are removed from the visible document during processing. Signers never
      see the tag text. They only see the interactive signing fields that replaced
      them.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Why use tags?</h2>
    <p>
      Tags automate field placement entirely, eliminating the need to manually
      specify pixel coordinates for every field on every page. Tags help in several
      scenarios:
    </p>
    <ul>
      <li>
        <strong>Template-driven workflows.</strong> When your system generates
        documents from templates, such as CRM merge fields or document assembly
        tools, embed tags in the template once and every generated document gets
        fields placed automatically.
      </li>
      <li>
        <strong>High-volume workflows.</strong> When you send hundreds or thousands
        of packages per day, manual field placement is impractical. Tags let you
        automate the process from document generation through sending.
      </li>
      <li>
        <strong>Migration from other platforms.</strong> If you are migrating from
        DocuSign or Adobe Sign, your existing documents likely already contain tags
        in those formats. The platform recognizes both natively, so existing
        templates work without modification.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <q-banner class="bg-red-1 text-red-9 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="priority_high" color="red-9" />
      </template>
      <strong>Text tags use 1-based signer indices.</strong> A tag with signer
      index <code>1</code> targets the first signer in the package, index
      <code>2</code> targets the second, and so on. This is the opposite
      convention from the Annotations API, which uses 0-based indices where
      <code>0</code> means first signer. When you place annotations
      programmatically, subtract one from the tag's number. This mismatch is the
      single most common integration error.
    </q-banner>

    <h2>Supported tag formats</h2>
    <p>
      The platform supports three tag formats. Use whichever best fits your
      integration, but do not mix formats within a single document. All three
      formats use 1-based signer indexing.
    </p>

    <h3>Platform format</h3>
    <p>The most expressive format, recommended for new integrations. The syntax is:</p>
    <GuideCodeTabs :samples="samples.platformSyntax" />
    <p>Examples, all targeting the first signer except where noted:</p>
    <GuideCodeTabs :samples="samples.platformExamples" />
    <p>
      The <code>signer</code> number is 1-based, where 1 is the first signer, 2 is
      the second, and so on. The <code>required</code> flag determines whether the
      signer must complete the field before finishing. The <code>label</code>
      appears as a tooltip or placeholder in the signing viewer. The trailing
      <code>options</code> field is a comma-separated list used by
      <code>Dropdown</code> and <code>RadioGroup</code> types. Leave it empty
      otherwise.
    </p>

    <h3>DocuSign-compatible format</h3>
    <p>
      Uses backslash-delimited or forward-slash-delimited short codes. The number
      refers to the signer index, 1-based in DocuSign convention.
    </p>
    <GuideCodeTabs :samples="samples.docusignExamples" />

    <h3>Adobe Sign-compatible format</h3>
    <p>
      Uses double-curly-brace syntax with an <code>_es_</code> namespace. The
      signer identifier follows Adobe Sign conventions.
    </p>
    <GuideCodeTabs :samples="samples.adobeExamples" />

    <q-separator class="q-my-lg" />

    <h2>Format comparison</h2>
    <p>
      The following table shows equivalent tags across all three formats for the
      most common field types. All examples target the first signer.
    </p>
    <table class="q-table q-mb-md" style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Field type</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Platform native</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">DocuSign</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Adobe Sign</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">Signature</td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Signature:1:true:Sign:}}</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>\s1\</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Sig_es_:signer1:signature}}</code></td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">Initials</td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Initials:1:true:Initials:}}</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>\i1\</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Int_es_:signer1:initials}}</code></td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">Date</td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Date:1:true:Date:}}</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>/d1/</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Dte_es_:signer1:date}}</code></td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">FullName</td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{FullName:1:false:Name:}}</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>/sn1/</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Nm_es_:signer1:fullname}}</code></td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">Text (2nd signer)</td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Text:2:true:Company:}}</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>\t2\</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code v-pre>{{Txt_es_:signer2:text}}</code></td>
        </tr>
      </tbody>
    </table>

    <q-separator class="q-my-lg" />

    <h2>Using tags in the portal</h2>
    <p>
      When you upload a document containing text tags via the package builder, the
      portal automatically detects them and offers to place the corresponding
      fields with one click. A notification appears at the top of the PDF
      annotator: "Text tags detected. Apply fields?" Click <strong>Apply</strong>
      and the fields appear at each tag's location, pre-assigned to the correct
      signer. You can then adjust, delete, or add more fields as needed.
    </p>
    <p>
      Tags themselves are invisible in the final rendered document. They are
      replaced by their corresponding fields during processing. Signers never see
      the tag text.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      If you are preparing documents programmatically, for example generating
      contracts from a CRM template with pre-placed signer tags, embed tags in the
      document source and use the detect-and-apply endpoints to create
      annotations. The tag utility API also provides standalone tools for
      validating, parsing, and converting tags between formats.
    </p>

    <h3>Auto-detection flow</h3>
    <p>
      Tag detection is a two-step process: first detect, then apply. The
      separation lets you review what the system found before committing to field
      creation.
    </p>

    <h3>Step 1. Upload the document</h3>
    <p>
      Upload your document to a package using the standard chunked upload flow.
      Wait for the document's <code>processingStatus</code> to reach
      <code>Ready</code>.
    </p>

    <h3>Step 2. Detect tags</h3>
    <p>
      Trigger tag detection on a specific document. You can optionally hint the
      expected format to improve accuracy. The system can also auto-detect.
    </p>
    <GuideCodeTabs :samples="samples.detectTags" />
    <p>
      The <code>format</code> field accepts <code>Platform</code>, <code>DocuSign</code>, or
      <code>AdobeSign</code>. Omit it to let the system auto-detect.
    </p>
    <p>Response:</p>
    <GuideCodeTabs :samples="samples.detectTagsResponse" />
    <p>
      The returned <code>signerIndex</code> is 1-based to match the tag. The
      returned <code>x</code>, <code>y</code>, <code>width</code>, and
      <code>height</code> are percentages of page dimensions, ready to store as-is
      on annotations.
    </p>

    <h3>Step 3. Apply detected tags</h3>
    <p>
      Once you are satisfied with the detection results, apply them to create
      annotation records for all detected tags across all documents in the
      package.
    </p>
    <GuideCodeTabs :samples="samples.applyDetectedTags" />
    <p>
      This creates the signing fields at the detected positions and removes the
      tag text from the document. The fields behave identically to manually placed
      fields from this point forward.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Tag utility API</h3>
    <p>
      The platform provides a standalone tag utility API that works independently
      of any package or document. These endpoints help with validating tags in
      your templates before uploading, converting between formats during
      migration, or building tags programmatically.
    </p>

    <h3>Validate a tag</h3>
    <p>
      Check if a tag string is syntactically correct. Always returns HTTP 200.
      Inspect the <code>isValid</code> field.
    </p>
    <GuideCodeTabs :samples="samples.validateTag" />

    <h3>Parse a tag</h3>
    <p>Break a tag into its component parts: type, 1-based signer index, required, label, and options.</p>
    <GuideCodeTabs :samples="samples.parseTag" />

    <h3>Build tags</h3>
    <p>Generate a tag in all three formats from its component values. The <code>signer</code> parameter is 1-based.</p>
    <GuideCodeTabs :samples="samples.buildTag" />

    <h3>Convert between formats</h3>
    <p>Convert a tag from one format to another. Valid format values: <code>Platform</code>, <code>DocuSign</code>, <code>AdobeSign</code>.</p>
    <GuideCodeTabs :samples="samples.convertTag" />

    <h3>Detect format</h3>
    <p>Identify which format a tag string uses. Returns <code>{ format: "Platform"|"DocuSign"|"AdobeSign"|null }</code>.</p>
    <GuideCodeTabs :samples="samples.detectFormat" />

    <h3>List supported field types</h3>
    <p>
      Retrieve all supported field types as the canonical registry, including
      which types accept user input versus being auto-filled, such as
      <code>DateSigned</code>, <code>FullName</code>, and <code>Email</code>.
    </p>
    <GuideCodeTabs :samples="samples.listTagTypes" />

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Embed tags in DOCX templates before upload</strong> for fully
        automated field placement. This eliminates the manual placement step and
        makes your integration end-to-end automated.
      </li>
      <li>
        <strong>Use Platform format for new integrations.</strong> The most
        expressive format, supporting labels, required flags, and options that
        the other formats lack.
      </li>
      <li>
        <strong>Use DocuSign or Adobe format for migration.</strong> If your
        existing templates already contain tags in those formats, the platform
        recognizes them natively. No rewriting needed.
      </li>
      <li>
        <strong>Always validate tags before embedding.</strong> Use the
        <code>POST /api/v1/tags/validate</code> endpoint to catch syntax errors
        early, before the document is uploaded and processed.
      </li>
      <li>
        <strong>Do not mix formats within a single document.</strong> The
        auto-detection system expects a consistent format per document. Mixing
        formats may leave some tags unrecognized.
      </li>
      <li>
        <strong>Tags are invisible to signers.</strong> Tag text is stripped
        during processing and replaced with interactive fields. Signers see clean
        signing fields, not placeholder strings.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mb-md" rounded>
      <strong>Tip:</strong> For high-volume integrations, combine text tags with
      the template system. Create a template with tag-embedded documents, and
      every package created from that template gets fields placed automatically
      with zero manual steps.
    </q-banner>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Tags</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

const samples = {
    // ─── Tag format reference (text-only, single tab) ───────────────────────────
    platformSyntax: {
        text: `{{type:signer:required:label:options}}`
    },

    platformExamples: {
        text: `{{Signature:1:true:Customer Signature:}}
{{Initials:1:true:Customer Initials:}}
{{Date:1:true:Date Signed:}}
{{FullName:1:false:Printed Name:}}
{{Text:2:true:Company Name:}}`
    },

    docusignExamples: {
        text: `\\s1\\      Signature for signer 1
\\i1\\      Initials for signer 1
/sn1/     Full name for signer 1
/d1/      Date signed for signer 1
\\t1\\      Text field for signer 1`
    },

    adobeExamples: {
        text: `{{Sig_es_:signer1:signature}}
{{Int_es_:signer1:initials}}
{{Nm_es_:signer1:fullname}}
{{Dte_es_:signer1:date}}
{{Txt_es_:signer1:text}}`
    },

    // ─── POST /packages/{id}/documents/{docId}/detect-tags ─────────────────────
    detectTags: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/documents/$DOC_ID/detect-tags \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "format": "Platform" }'`,

        js: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/documents/\${docId}/detect-tags\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({ format: 'Platform' })
  }
);
const { detectedFormat, tags, unrecognizedTagCount } = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/documents/\${docId}/detect-tags\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ format: 'Platform' })
  }
);
const { detectedFormat, tags } = await response.json();
console.log(\`Detected \${tags.length} tags in \${detectedFormat} format\`);`,

        python: `response = requests.post(
    f'${API}/api/v1/packages/{package_id}/documents/{doc_id}/detect-tags',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'format': 'Platform'}
)
response.raise_for_status()
result = response.json()
print(f"Detected {len(result['tags'])} tags in {result['detectedFormat']} format")`,

        csharp: `var response = await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/documents/{docId}/detect-tags",
    new { format = "Platform" });
response.EnsureSuccessStatusCode();
var result = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post(
    "api/v1/packages/{$packageId}/documents/{$docId}/detect-tags",
    [
        'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
        'json' => ['format' => 'Platform'],
    ]
);
$result = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/documents/#{doc_id}/detect-tags")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { format: 'Platform' }.to_json
result = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    detectTagsResponse: {
        json: `{
  "detectedFormat": "Platform",
  "tags": [
    {
      "rawTag": "{{Signature:1:true:Customer Signature:}}",
      "type": "Signature",
      "signerIndex": 1,
      "required": true,
      "label": "Customer Signature",
      "pageNumber": 1,
      "x": 12.4, "y": 82.1,
      "width": 25.0, "height": 6.0
    }
  ],
  "unrecognizedTagCount": 0
}`
    },

    applyDetectedTags: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/apply-detected-tags \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/apply-detected-tags\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const { annotations, count } = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/apply-detected-tags\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const { count } = await response.json();
console.log(\`Applied \${count} annotations from detected tags\`);`,

        python: `response = requests.post(
    f'${API}/api/v1/packages/{package_id}/apply-detected-tags',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
print(f"Applied {response.json()['count']} annotations")`,

        csharp: `var response = await client.PostAsync(
    $"api/v1/packages/{packageId}/apply-detected-tags", null);
response.EnsureSuccessStatusCode();`,

        php: `$client->post("api/v1/packages/{$packageId}/apply-detected-tags", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/apply-detected-tags")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Tag utility API ────────────────────────────────────────────────────────
    validateTag: {
        curl: `curl -X POST ${API}/api/v1/tags/validate \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "tag": "{{Signature:1:true:Sign Here:}}" }'`,

        js: `const response = await fetch('${API}/api/v1/tags/validate', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ tag: '{{Signature:1:true:Sign Here:}}' })
});
const { isValid, detectedFormat, errorMessage, parsedTag } = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/tags/validate', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ tag: '{{Signature:1:true:Sign Here:}}' })
});
const result = await response.json();`,

        python: `response = requests.post(
    '${API}/api/v1/tags/validate',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'tag': '{{Signature:1:true:Sign Here:}}'}
)
result = response.json()`,

        csharp: `var response = await client.PostAsJsonAsync(
    "api/v1/tags/validate",
    new { tag = "{{Signature:1:true:Sign Here:}}" });
var result = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post('api/v1/tags/validate', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['tag' => '{{Signature:1:true:Sign Here:}}'],
]);
$result = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/tags/validate')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { tag: '{{Signature:1:true:Sign Here:}}' }.to_json
result = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    parseTag: {
        curl: `curl -X POST ${API}/api/v1/tags/parse \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "tag": "\\\\s1\\\\" }'`,

        js: `const response = await fetch('${API}/api/v1/tags/parse', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ tag: '\\\\s1\\\\' })
});
const parsed = await response.json();
// { type, signerIndex (1-based), required, label, options }`,

        node: `const response = await fetch('${API}/api/v1/tags/parse', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ tag: '\\\\s1\\\\' })
});
const parsed = await response.json();`,

        python: `response = requests.post(
    '${API}/api/v1/tags/parse',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'tag': '\\\\s1\\\\'}
)
response.raise_for_status()
parsed = response.json()`,

        csharp: `var response = await client.PostAsJsonAsync(
    "api/v1/tags/parse",
    new { tag = @"\\s1\\" });
response.EnsureSuccessStatusCode();
var parsed = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post('api/v1/tags/parse', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['tag' => '\\\\s1\\\\'],
]);
$parsed = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/tags/parse')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { tag: '\\\\s1\\\\' }.to_json
parsed = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    buildTag: {
        curl: `curl "${API}/api/v1/tags/build?type=Signature&signer=1&required=true&label=Sign%20Here" \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const url = new URL('${API}/api/v1/tags/build');
url.search = new URLSearchParams({
  type: 'Signature',
  signer: '1',
  required: 'true',
  label: 'Sign Here'
}).toString();

const response = await fetch(url, { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } });
const { Platform, DocuSign, AdobeSign } = await response.json();`,

        node: `const url = new URL('${API}/api/v1/tags/build');
url.search = new URLSearchParams({
  type: 'Signature',
  signer: '1',
  required: 'true',
  label: 'Sign Here'
}).toString();

const response = await fetch(url, { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } });
const { Platform, DocuSign, AdobeSign } = await response.json();`,

        python: `response = requests.get(
    '${API}/api/v1/tags/build',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    params={'type': 'Signature', 'signer': 1, 'required': 'true', 'label': 'Sign Here'}
)
response.raise_for_status()
tag = response.json()`,

        csharp: `var qs = "?type=Signature&signer=1&required=true&label=Sign%20Here";
var response = await client.GetFromJsonAsync<JsonElement>($"api/v1/tags/build{qs}");`,

        php: `$response = $client->get('api/v1/tags/build', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'query' => [
        'type' => 'Signature',
        'signer' => 1,
        'required' => 'true',
        'label' => 'Sign Here',
    ],
]);
$tag = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/tags/build')
uri.query = URI.encode_www_form(
  type: 'Signature', signer: 1, required: 'true', label: 'Sign Here'
)
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
tag = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    convertTag: {
        curl: `curl -X POST ${API}/api/v1/tags/convert \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "tag": "\\\\s1\\\\", "targetFormat": "Platform" }'`,

        js: `const response = await fetch('${API}/api/v1/tags/convert', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ tag: '\\\\s1\\\\', targetFormat: 'Platform' })
});
const { tag, format } = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/tags/convert', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ tag: '\\\\s1\\\\', targetFormat: 'Platform' })
});
const { tag, format } = await response.json();`,

        python: `response = requests.post(
    '${API}/api/v1/tags/convert',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'tag': '\\\\s1\\\\', 'targetFormat': 'Platform'}
)
response.raise_for_status()
result = response.json()`,

        csharp: `var response = await client.PostAsJsonAsync(
    "api/v1/tags/convert",
    new { tag = @"\\s1\\", targetFormat = "Platform" });
response.EnsureSuccessStatusCode();
var result = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post('api/v1/tags/convert', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['tag' => '\\\\s1\\\\', 'targetFormat' => 'Platform'],
]);
$result = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/tags/convert')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { tag: '\\\\s1\\\\', targetFormat: 'Platform' }.to_json
result = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    detectFormat: {
        curl: `curl "${API}/api/v1/tags/detect-format?tag=%7B%7BSignature%3A1%3Atrue%3ASign%3A%7D%7D" \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const url = new URL('${API}/api/v1/tags/detect-format');
url.searchParams.set('tag', '{{Signature:1:true:Sign:}}');

const response = await fetch(url, { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } });
const { format } = await response.json();`,

        node: `const url = new URL('${API}/api/v1/tags/detect-format');
url.searchParams.set('tag', '{{Signature:1:true:Sign:}}');

const response = await fetch(url, { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } });
const { format } = await response.json();`,

        python: `response = requests.get(
    '${API}/api/v1/tags/detect-format',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    params={'tag': '{{Signature:1:true:Sign:}}'}
)
format_name = response.json()['format']`,

        csharp: `var tagParam = Uri.EscapeDataString("{{Signature:1:true:Sign:}}");
var response = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/tags/detect-format?tag={tagParam}");`,

        php: `$response = $client->get('api/v1/tags/detect-format', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'query' => ['tag' => '{{Signature:1:true:Sign:}}'],
]);
$format = json_decode($response->getBody(), true)['format'];`,

        ruby: `uri = URI('${API}/api/v1/tags/detect-format')
uri.query = URI.encode_www_form(tag: '{{Signature:1:true:Sign:}}')
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
format = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)['format']`
    },

    listTagTypes: {
        curl: `curl ${API}/api/v1/tags/types \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch('${API}/api/v1/tags/types', {
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN }
});
const types = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/tags/types', {
  headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN }
});
const types = await response.json();`,

        python: `types = requests.get(
    '${API}/api/v1/tags/types',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
).json()`,

        csharp: `var types = await client.GetFromJsonAsync<JsonElement>("api/v1/tags/types");`,

        php: `$response = $client->get('api/v1/tags/types', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$types = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/tags/types')
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
types = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    }
};

export default {
    name: 'GuideTextTags',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>

